import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { formatMarkdownRichText } from '../../../utils/formatRichText';

const SeoSubectExaplainerCTA = ({ data }) => {
  if (!data) return null;

  const { title, card } = data;
  if (!card) return null;

  const { title: cardTitle, body = '' } = card;
  const sectionRef = useRef(null);
  const blueCardRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) return undefined;

    const sectionEl = sectionRef.current;
    const blueEl = blueCardRef.current;

    if (!sectionEl || !blueEl) return undefined;

    const maxShift = 18;
    let rafId = null;

    const updateBlueOffset = () => {
      rafId = null;

      const rect = sectionEl.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const start = viewportHeight;
      const end = -rect.height;
      const rawProgress = (start - rect.top) / (start - end);
      const progress = Math.min(1, Math.max(0, rawProgress));
      const offset = Math.sin(progress * Math.PI) * maxShift;

      blueEl.style.transform = `translate3d(0, ${offset}px, 0)`;
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateBlueOffset);
    };

    updateBlueOffset();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: { xs: '16px', md: '24px' },
        mb: { xs: '24px', md: '48px' },
        boxSizing: 'border-box',
        backgroundColor: '#F3F6F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1010px',
          position: 'relative',
          overflow: 'visible',
          boxSizing: 'border-box',
          minHeight: { xs: 'auto', md: '559px' },
          pb: { xs: '16px', md: '0px' },
        }}
      >
        <Box
          ref={blueCardRef}
          sx={{
            width: { xs: '100%', md: '493px' },
            minHeight: { xs: 'auto', md: '242px' },
            backgroundColor: '#162F3B',
            borderRadius: '34px',
            boxSizing: 'border-box',
            p: { xs: '32px 24px', md: '40px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            position: { xs: 'relative', md: 'sticky' },
            top: { xs: '0px', md: '120px' },
            mt: { xs: '0px', md: '40px' },
            mb: { xs: '0px', md: '40px' },
            left: { xs: '0px', md: '0px' },
            zIndex: 2,
            willChange: 'transform',
            transform: 'translate3d(0, 0, 0)',
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#FFFFFF',
              whiteSpace: 'pre-line',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '596px' },
            minHeight: { xs: 'auto', md: '559px' },
            backgroundColor: '#FFFFFF',
            borderRadius: '34px',
            boxSizing: 'border-box',
            p: { xs: '24px', md: '40px 80px 40px 160px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '20px', md: '40px' },
            position: { xs: 'relative', md: 'absolute' },
            top: { xs: '16px', md: '0px' },
            left: { xs: '0px', md: '414px' },
            zIndex: 1,
            boxShadow: '0px 12px 24px -4px rgba(22, 47, 59, 0.08)',
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              whiteSpace: 'pre-line',
              maxWidth: '356px',
            }}
          >
            {cardTitle}
          </Typography>

          <Typography
            component="div"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.002em',
              color: '#162F3B',
              whiteSpace: 'pre-line',
              maxWidth: '356px',
              '& p': {
                m: 0,
                mb: '1em',
              },
              '& p:last-of-type': {
                mb: 0,
              },
              '& ul': {
                m: 0,
                pl: '20px',
              },
              '& li': {
                mb: '0.5em',
              },
            }}
            dangerouslySetInnerHTML={{ __html: formatMarkdownRichText(body) }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SeoSubectExaplainerCTA;
